import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/shadcn/Tooltip/Tooltip";
import type { ChatTimelineItemProps } from "./ChatTimelineItem";
import { sandboxToolLabel } from "./sandboxToolPresentation";
import { ChatActivityIndicator } from "@/components/ui/AiChatPanel/ChatActivityIndicator";
import { CircleCheck, CircleMinus, CircleAlert } from "lucide-react";

export function ChatToolItem({
  item,
  grouped = false,
}: Pick<ChatTimelineItemProps, "item"> & { grouped?: boolean }) {
  const isLoadedSkill =
    item.status === "completed" &&
    item.payload.activityType === "skill_load" &&
    typeof item.payload.skillName === "string" &&
    item.payload.skillName.trim().length > 0;
  const suppliedLabel =
    typeof item.payload.label === "string" && item.payload.label.trim()
      ? item.payload.label
      : (item.text ?? "LawWorks action");
  const nativeLabel = sandboxToolLabel(item.payload.toolName);
  const label = isLoadedSkill
    ? `Loaded skill: ${item.payload.skillName}`
    : nativeLabel &&
        [
          "working with files",
          "lawworks action",
          String(item.payload.toolName).toLowerCase(),
        ].includes(suppliedLabel.toLowerCase())
      ? nativeLabel
      : suppliedLabel;
  const isActive = item.status === "queued" || item.status === "running";
  const needsConfirmation =
    item.status === "failed" &&
    item.payload.guardianReviewStatus === "rejected" &&
    item.payload.guardianRiskLevel === "high";
  const confirmationMessage =
    "This action was assessed as high risk. We’re confirming that you intended it.";
  const status =
    item.status === "queued"
      ? "Queued"
      : item.status === "running"
        ? "Running"
        : item.status === "cancelled"
          ? "Cancelled"
          : needsConfirmation
            ? "Needs confirmation"
            : item.status === "failed"
              ? "Failed"
              : "Finished";

  return (
    <div
      data-chat-item-id={grouped ? item.id : undefined}
      className="text-muted-foreground flex min-h-7 max-w-full min-w-0 items-center gap-2 py-0.5 text-[13px] leading-6"
    >
      {needsConfirmation ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                role="img"
                aria-label={confirmationMessage}
                className="focus-visible:ring-ring rounded-full focus-visible:ring-2 focus-visible:outline-none"
              >
                <CircleAlert className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {confirmationMessage}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <ChatActivityIndicator
          active={isActive}
          className="text-muted-foreground size-4"
        >
          {item.status === "cancelled" ? (
            <CircleMinus className="size-4" />
          ) : item.status === "failed" ? (
            <CircleAlert className="text-destructive size-4" />
          ) : (
            <CircleCheck className="size-4" />
          )}
        </ChatActivityIndicator>
      )}
      <span
        data-chat-search-content
        className={
          isLoadedSkill
            ? "min-w-0 [overflow-wrap:anywhere]"
            : "min-w-0 truncate"
        }
      >
        {label}
      </span>
      {!isLoadedSkill && (
        <span className="text-muted-foreground shrink-0 text-xs">{status}</span>
      )}
    </div>
  );
}
